import React, { useEffect, useMemo, useState } from 'react';
import Confetti from './Confetti';
import rabbiLogo from '../assets/rabbi.png';

// Displays the outcome of a round including each player's interpretation score
function RoundResult({
  question,
  rabiAnswer,
  scores,
  winnerId,
  winnerReason,
  players,
  socketId: clientId,
  onReady,
  copy = {},
  language = 'he',
  common = {},
}) {
  const nameMap = useMemo(
    () => Object.fromEntries((players || []).map(p => [p.id, p.name])),
    [players]
  );
  const entries = useMemo(
    () =>
      Object.entries(scores || {}).sort(
        (a, b) => (b[1]?.score ?? 0) - (a[1]?.score ?? 0)
      ),
    [scores]
  );
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState(null);

  const direction = language === 'he' ? 'rtl' : 'ltr';
  const youLabel = common.you || (language === 'he' ? 'את/ה' : 'You');
  const title = copy.title || (language === 'he' ? 'תוצאות הסיבוב' : 'Round Results');
  const winnerLabel = copy.winnerLabel || (language === 'he' ? 'הזוכה:' : 'Winner:');
  const tableHeaders = {
    player: copy.tableHeaders?.player || (language === 'he' ? 'שחקן' : 'Player'),
    interpretation: copy.tableHeaders?.interpretation || (language === 'he' ? 'פרשנות' : 'Interpretation'),
    score: copy.tableHeaders?.score || (language === 'he' ? 'ניקוד' : 'Score'),
    reason: copy.tableHeaders?.reason || (language === 'he' ? 'נימוק' : 'Reason'),
  };
  const winnerReasonLabel = copy.winnerReasonLabel || (language === 'he' ? 'נימוק הזכייה:' : 'Winning reason:');
  const readyButton = copy.readyButton || (language === 'he' ? 'מוכן/ה לסיבוב הבא' : 'Ready for the next round');
  const shareButton = copy.shareButton || (language === 'he' ? 'שתף את הניצחון' : 'Share your victory');
  const shareModalClose = copy.shareModalClose || (language === 'he' ? 'סגור' : 'Close');
  const shareCopyButton =
    copy.shareCopyButton || (language === 'he' ? 'העתק הודעת שיתוף' : 'Copy share message');
  const shareCopyError =
    copy.shareCopyError ||
    (language === 'he'
      ? 'אירעה שגיאה בעת העתקת ההודעה. נסו שוב.'
      : 'Something went wrong while copying the share message. Please try again.');

  // Preload logo as data URL to avoid tainted canvas during capture
  useEffect(() => {
    let isMounted = true;
    const toDataUrl = async (url) => {
      try {
        const res = await fetch(url, { mode: 'cors' });
        const blob = await res.blob();
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };
    (async () => {
      const d = await toDataUrl(rabbiLogo);
      if (isMounted) setLogoDataUrl(d);
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  // Frontend fallback: compute winner from scores if backend did not provide one
  const computedWinnerId = winnerId || (entries[0] ? entries[0][0] : null);
  const winnerName = computedWinnerId
    ? (computedWinnerId === clientId ? youLabel : (nameMap[computedWinnerId] || computedWinnerId))
    : '';
  const winnerEntry = computedWinnerId
    ? entries.find(([id]) => id === computedWinnerId)?.[1] || scores?.[computedWinnerId]
    : null;
  const defaultGameUrl = 'https://rabbi-game.com';
  const shareGameUrl = copy.shareGameUrl || defaultGameUrl;

  const buildShareAsciiMessage = () => {
    if (!winnerEntry) {
      return '';
    }

    const winnerScore = winnerEntry.score ?? '';
    const winnerText = winnerEntry.text ?? '';
    const safeQuestion = question ?? '';
    const safeAnswer = rabiAnswer ?? '';
    const safeUrl = shareGameUrl ?? '';
    const hasScore = winnerScore !== '' && winnerScore !== null;

    if (language === 'he') {
      return [
        'ניצחון סוחף! 🏆',
        `השאלה: ${safeQuestion}`.trim(),
        `הרב ענה: ${safeAnswer}`.trim(),
        `הפרשנות שלי: ${winnerText}`.trim(),
        hasScore ? `הציון שלי: ${winnerScore} `+`⭐`.repeat(parseInt(winnerScore)) : '',
        'הרבי תמיד צודק—והפעם הוא היה חד במיוחד.',
        `בואו לשחק גם אתם: ${safeUrl}`.trim(),
      ]
        .filter(Boolean)
        .join('\n');
    }

    return [
      'I won this round! 🏆',
      `The question: ${safeQuestion}`.trim(),
      `The Rabbi answered: ${safeAnswer}`.trim(),
      `My interpretation: ${winnerText}`.trim(),
      hasScore ? `My score: ${winnerScore} `+`⭐`.repeat(parseInt(winnerScore)): '',
      'The Rabbi is always right — and today, razor sharp.',
      `Come play too: ${safeUrl}`.trim(),
    ]
      .filter(Boolean)
      .join('\n');
  };

  const handleOpenShare = () => {
    setShareError(null);
    setIsShareOpen(true);
  };

  const handleCloseShare = () => {
    setIsShareOpen(false);
    setShareError(null);
  };

  const handleCopyShare = async () => {
    if (!winnerEntry) {
      return;
    }

    const shareMessage = buildShareAsciiMessage();
    if (!shareMessage) {
      return;
    }

    try {
      setShareError(null);
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareMessage);
      } else if (typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = shareMessage;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        const selection = document.getSelection();
        const selectedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        if (selectedRange && selection) {
          selection.removeAllRanges();
          selection.addRange(selectedRange);
        }
      } else {
        throw new Error('Clipboard API is not available');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy share message', error);
      setShareError(shareCopyError);
    }
  };

  const shareOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
    boxSizing: 'border-box',
  };

  const shareContainerStyle = {
    position: 'relative',
    width: 'min(90vw, 440px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  };

  const shareCardStyle = {
    position: 'relative',
    borderRadius: '22px',
    padding: '3rem 2.5rem',
    boxShadow: '0 25px 55px rgba(0, 0, 0, 0.28)',
    backgroundImage: `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.90) 35%, rgba(255,255,255,0.88) 100%), url(${logoDataUrl || rabbiLogo})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: '#0f172a',
    fontFamily: `"Heebo", system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"`,
    textAlign: 'left',
    overflow: 'hidden',
  };

  const shareTextRowStyle = {
    marginBottom: '0.7rem',
    lineHeight: 1.55,
    fontSize: '1.06rem',
  };

  const shareActionsStyle = {
    display: 'flex',
    flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
    gap: '0.75rem',
    justifyContent: 'center',
  };

  const actionButtonBase = {
    border: 'none',
    borderRadius: '999px',
    padding: '0.6rem 1.6rem',
    fontSize: '0.95rem',
    cursor: 'pointer',
    fontWeight: 600,
  };

  const showShareButton = computedWinnerId && computedWinnerId === clientId && winnerEntry;

  const stackedButtonStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    marginTop: showShareButton ? '1rem' : '0.25rem',
  };

  const fullWidthButton = {
    width: '100%',
    maxWidth: '320px',
  };

  return (
    <div style={{ textAlign: 'center' }} dir={direction}>
      {(computedWinnerId && computedWinnerId === clientId) && <Confetti />}
      <h2>{title}</h2>
      {computedWinnerId && (
        <h3 style={{ color: '#28a745' }}>
          {winnerLabel} {winnerName}
        </h3>
      )}
      <h3>{question}</h3>
      <p><strong>{rabiAnswer}</strong></p>
      <table style={{ margin: '0 auto', borderCollapse: 'collapse', minWidth: '60%' }}>
        <thead>
          <tr>
            <th style={{ borderBottom: '2px solid #ccc', padding: '0.5rem' }}>{tableHeaders.player}</th>
            <th style={{ borderBottom: '2px solid #ccc', padding: '0.5rem' }}>{tableHeaders.interpretation}</th>
            <th style={{ borderBottom: '2px solid #ccc', padding: '0.5rem' }}>{tableHeaders.score}</th>
            <th style={{ borderBottom: '2px solid #ccc', padding: '0.5rem' }}>{tableHeaders.reason}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([id, { text, score, reason }]) => (
            <tr
              key={id}
              style={{
                fontWeight: id === computedWinnerId ? 'bold' : 'normal',
                backgroundColor: id === computedWinnerId ? '#fff9c4' : 'transparent',
              }}
            >
              <td style={{ borderBottom: '1px solid #eee', padding: '0.5rem' }}>
                {id === clientId ? youLabel : (nameMap[id] || id)}
                {id === computedWinnerId && ' 🏆'}
              </td>
              <td style={{ borderBottom: '1px solid #eee', padding: '0.5rem' }}>{text}</td>
              <td style={{ borderBottom: '1px solid #eee', padding: '0.5rem' }}>{score}</td>
              <td style={{ borderBottom: '1px solid #eee', padding: '0.5rem' }}>{reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {winnerReason && (
        <p style={{ marginTop: '1rem' }}>
          <strong>{winnerReasonLabel}</strong> {winnerReason}
        </p>
      )}
      <div style={stackedButtonStyle}>
        {showShareButton && (
          <button
            type="button"
            onClick={handleOpenShare}
            style={{
              backgroundColor: '#16a34a',
              color: '#fff',
              border: 'none',
              borderRadius: '999px',
              padding: '0.5rem 1.4rem',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
              boxShadow: '0 8px 18px rgba(22, 163, 74, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem',
              ...fullWidthButton,
            }}
          >
            <span aria-hidden="true" style={{ display: 'inline-flex' }}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: 'block' }}
              >
                <path
                  d="M18 16a3 3 0 0 0-2.824 2H8.824a3 3 0 1 0 0 2h6.352A3 3 0 1 0 18 16Zm0 4a1 1 0 1 1 0-2 1 1 0 0 1 0 2ZM6 20a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm12-16a3 3 0 0 0-2.824 2H8.824a3 3 0 1 0 0 2h6.352A3 3 0 1 0 18 4Zm0 4a1 1 0 1 1 0-2 1 1 0 0 1 0 2ZM6 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm6 2a3 3 0 0 0-2.824 2H6a1 1 0 0 0 0 2h3.176A3 3 0 1 0 12 10Zm0 4a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <span>{shareButton}</span>
          </button>
        )}
        <button onClick={onReady} style={{ ...fullWidthButton }}>
          {readyButton}
        </button>
      </div>
      {isShareOpen && winnerEntry && (
        <div style={shareOverlayStyle} dir={direction}>
          <div style={shareContainerStyle}>
            <div style={{ height: 0 }}></div>
            <div style={shareCardStyle}>
              <button
                type="button"
                onClick={handleCloseShare}
                aria-label={shareModalClose}
                style={{
                  position: 'absolute',
                  top: '10px',
                  [direction === 'rtl' ? 'left' : 'right']: '10px',
                  background: 'rgba(15,23,42,0.08)',
                  color: '#0f172a',
                  border: 'none',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%'
                }}
              >
                ✕
              </button>
              <div style={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                <p style={{
                  ...shareTextRowStyle,
                  marginTop: '0.2rem',
                  fontWeight: 800,
                  fontSize: '1.5rem',
                  letterSpacing: '0.2px',
                  color: '#0b5ed7'
                }}>
                  {language === 'he' ? 'ניצחון סוחף! 🏆' : 'I won this round! 🏆'}
                </p>
                <p style={{ ...shareTextRowStyle, opacity: 0.9 }}>
                  <strong>{language === 'he' ? 'השאלה:' : 'The question:'}</strong> {question}
                </p>
                <p style={{ ...shareTextRowStyle, opacity: 0.95 }}>
                  <strong>{language === 'he' ? 'הרב ענה:' : 'The Rabbi answered:'}</strong> {rabiAnswer}
                </p>
                <p style={{ ...shareTextRowStyle, opacity: 0.95 }}>
                  <strong>{language === 'he' ? 'הפרשנות שלי:' : 'My interpretation:'}</strong> {winnerEntry.text}
                </p>
                <p style={{ ...shareTextRowStyle, fontWeight: 700 }}>
                  {language === 'he' ? 'הציון שלי:' : 'My score:'} {winnerEntry.score} {'⭐'.repeat(winnerEntry.score)}
                </p>
                <p style={{ ...shareTextRowStyle, marginTop: '0.4rem', color: '#334155' }}>
                  {language === 'he'
                    ? 'הרבי תמיד צודק—והפעם הוא היה חד במיוחד.'
                    : 'The Rabbi is always right — and today, razor sharp.'}
                </p>
                <p style={{ ...shareTextRowStyle, marginTop: '0.1rem' }}>
                  {language === 'he' ? 'בואו לשחק גם אתם:' : 'Come play too:'} {shareGameUrl}
                </p>
              </div>
            </div>
            {shareError && (
              <div
                style={{
                  backgroundColor: 'rgba(220, 38, 38, 0.12)',
                  color: '#991b1b',
                  borderRadius: '12px',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                }}
              >
                {shareError}
              </div>
            )}
            <div style={shareActionsStyle}>
              <button
                type="button"
                onClick={handleCopyShare}
                style={{
                  ...actionButtonBase,
                  backgroundColor: '#0f766e',
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                }}
              >
                <span aria-hidden="true" style={{ fontSize: '1.1rem', lineHeight: 1 }}>
                  📋
                </span>
                <span>{shareCopyButton}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoundResult;
